import React from 'react';
import { Text, type TextProps } from 'react-native';

import { getFontFamily, type FontWeight } from '@/config/theme';
import { useLocale } from '@/lib/i18n/LocaleProvider';

export type TextVariant = 'display' | 'title' | 'heading' | 'body' | 'caption' | 'micro';

const VARIANT_CLASSES: Record<TextVariant, string> = {
  display: 'text-4xl text-ink',
  title: 'text-2xl text-ink',
  heading: 'text-lg text-ink',
  body: 'text-base text-ink',
  caption: 'text-sm text-muted',
  micro: 'text-xs text-muted',
};

const VARIANT_WEIGHTS: Record<TextVariant, FontWeight> = {
  display: 'bold',
  title: 'bold',
  heading: 'semibold',
  body: 'regular',
  caption: 'regular',
  micro: 'medium',
};

/** Display + title use the display face (Latin only - Arabic keeps Plex). */
const DISPLAY_VARIANTS: ReadonlySet<TextVariant> = new Set(['display', 'title']);

export interface AppTextProps extends TextProps {
  variant?: TextVariant;
  weight?: FontWeight;
  /** Tabular figures for scores/stats so digits align in columns. */
  tabular?: boolean;
  className?: string;
}

export function AppText({
  variant = 'body',
  weight,
  tabular = false,
  className,
  style,
  ...rest
}: AppTextProps) {
  const { locale } = useLocale();
  const fontFamily = getFontFamily(
    locale,
    weight ?? VARIANT_WEIGHTS[variant],
    DISPLAY_VARIANTS.has(variant),
  );
  return (
    <Text
      className={`${VARIANT_CLASSES[variant]}${className ? ` ${className}` : ''}`}
      style={[{ fontFamily }, tabular ? { fontVariant: ['tabular-nums'] } : null, style]}
      {...rest}
    />
  );
}
